import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react'

/**
 * Widget do Turnstile, em renderização EXPLÍCITA.
 *
 * O modo automático (`class="cf-turnstile"`, varredura do DOM) é o exemplo da
 * documentação, mas não serve a uma SPA: o script varre o documento uma vez, e
 * em React o mesmo nó monta e desmonta várias vezes — o resultado é widget
 * duplicado ou widget que some ao trocar de etapa. Com `render=explicit`
 * controlamos o ciclo de vida junto com o do componente.
 */

interface OpcoesRender {
  sitekey: string
  action?: string
  callback?: (token: string) => void
  'expired-callback'?: () => void
  'timeout-callback'?: () => void
  'error-callback'?: (codigo?: string) => void
  theme?: 'light' | 'dark' | 'auto'
  language?: string
  appearance?: 'always' | 'execute' | 'interaction-only'
}

interface ApiTurnstile {
  render: (elemento: HTMLElement, opcoes: OpcoesRender) => string | undefined
  reset: (id?: string) => void
  remove: (id?: string) => void
}

declare global {
  interface Window {
    turnstile?: ApiTurnstile
  }
}

const URL_SCRIPT = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'

let carregamento: Promise<ApiTurnstile> | null = null

/** Carrega o script uma única vez, por mais que o componente remonte. */
function carregarScript(): Promise<ApiTurnstile> {
  if (window.turnstile) return Promise.resolve(window.turnstile)
  if (carregamento) return carregamento

  carregamento = new Promise<ApiTurnstile>((resolver, rejeitar) => {
    const script = document.createElement('script')
    script.src = URL_SCRIPT
    script.async = true
    script.defer = true
    script.onload = () => {
      if (window.turnstile) resolver(window.turnstile)
      else rejeitar(new Error('Turnstile carregou sem expor a API.'))
    }
    script.onerror = () => {
      /* Zera para que uma nova tentativa possa recarregar — bloqueio de rede
         momentâneo não deve deixar o formulário travado para sempre. */
      carregamento = null
      rejeitar(new Error('Não foi possível carregar a verificação de segurança.'))
    }
    document.head.appendChild(script)
  })

  return carregamento
}

export interface TurnstileHandle {
  /**
   * Descarta o token atual e pede outro.
   *
   * Obrigatório após CADA tentativa de envio, dando certo ou errado: o token é
   * de uso único e vale 300s. Sem o reset, a segunda tentativa manda um token
   * já gasto e a Cloudflare responde `timeout-or-duplicate`.
   */
  reiniciar: () => void
}

interface Props {
  sitekey: string
  action: string
  /** Recebe o token, ou `null` quando expira, falha ou é reiniciado. */
  aoObterToken: (token: string | null) => void
  aoFalhar?: (mensagem: string) => void
}

export const Turnstile = forwardRef<TurnstileHandle, Props>(function Turnstile(
  { sitekey, action, aoObterToken, aoFalhar },
  ref,
) {
  const container = useRef<HTMLDivElement | null>(null)
  const idWidget = useRef<string | undefined>(undefined)

  /* Em refs para que o efeito de montagem NÃO dependa deles: o consumidor
     costuma passar função inline, e a dependência remontaria o widget a cada
     render — perdendo o token que o usuário acabou de resolver. */
  const aoObterTokenRef = useRef(aoObterToken)
  const aoFalharRef = useRef(aoFalhar)
  aoObterTokenRef.current = aoObterToken
  aoFalharRef.current = aoFalhar

  useImperativeHandle(ref, () => ({
    reiniciar() {
      if (idWidget.current && window.turnstile) {
        window.turnstile.reset(idWidget.current)
        aoObterTokenRef.current(null)
      }
    },
  }))

  useEffect(() => {
    let vivo = true

    carregarScript()
      .then((api) => {
        if (!vivo || !container.current) return
        idWidget.current = api.render(container.current, {
          sitekey,
          action,
          theme: 'light',
          language: 'pt-br',
          callback: (token) => aoObterTokenRef.current(token),
          'expired-callback': () => aoObterTokenRef.current(null),
          'timeout-callback': () => aoObterTokenRef.current(null),
          'error-callback': (codigo) => {
            aoObterTokenRef.current(null)
            aoFalharRef.current?.(
              `Falha na verificação de segurança${codigo ? ` (${codigo})` : ''}. Tente novamente.`,
            )
          },
        })
      })
      .catch((erro: Error) => {
        if (vivo) aoFalharRef.current?.(erro.message)
      })

    return () => {
      vivo = false
      if (idWidget.current && window.turnstile) {
        window.turnstile.remove(idWidget.current)
        idWidget.current = undefined
      }
    }
  }, [sitekey, action])

  return <div ref={container} />
})
